// A short chime for DMs and mentions, made with Web Audio so nothing is
// fetched, and works on plain HTTP. People turn it off in Settings; the
// choice is kept in this browser.

const key = "lantern.sound";
let ctx: AudioContext | null = null;
let last = -Infinity;

export function soundOn(): boolean {
  try {
    return localStorage.getItem(key) !== "off";
  } catch {
    return true;
  }
}

export function setSound(on: boolean) {
  try {
    localStorage.setItem(key, on ? "on" : "off");
  } catch {
    // Private windows may refuse; the choice lasts until reload.
  }
}

// unlockSound readies audio on the first click or key press, since
// browsers let a page play sound only after the person interacts with it.
export function unlockSound() {
  const unlock = () => {
    if (typeof AudioContext === "undefined") return;
    ctx ??= new AudioContext();
    void ctx.resume().catch(() => {});
  };
  addEventListener("pointerdown", unlock, { once: true });
  addEventListener("keydown", unlock, { once: true });
}

// chime plays two soft rising notes, at most once in 1.5 seconds so a burst
// of messages rings once. force plays it even when sound is off, to try it.
export function chime(force = false) {
  if ((!force && !soundOn()) || !ctx || ctx.state !== "running") return;
  const now = performance.now();
  if (!force && now - last < 1500) return;
  last = now;
  const t = ctx.currentTime;
  [880, 1320].forEach((freq, i) => {
    const osc = ctx!.createOscillator();
    const gain = ctx!.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const start = t + i * 0.11;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.12, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.25);
    osc.connect(gain).connect(ctx!.destination);
    osc.start(start);
    osc.stop(start + 0.26);
  });
}

// tryChime plays the chime from a click, readying audio first.
export function tryChime() {
  if (typeof AudioContext === "undefined") return;
  ctx ??= new AudioContext();
  void ctx.resume().then(() => chime(true), () => {});
}

// ring plays a call's ringing until the returned function stops it: a
// rising three notes every 3 seconds for a call coming in, and a low,
// quiet ringback every 4 seconds for one's own call while nobody has
// answered. It is silent when sound is off.
export function ring(kind: "incoming" | "outgoing"): () => void {
  const notes = kind === "incoming" ? [660, 880, 1100] : [440];
  const every = kind === "incoming" ? 3000 : 4000;
  const play = () => {
    if (!soundOn() || !ctx || ctx.state !== "running") return;
    const t = ctx.currentTime;
    notes.forEach((freq, i) => {
      const start = t + i * 0.16;
      const length = kind === "incoming" ? 0.3 : 1.2;
      const osc = ctx!.createOscillator();
      const gain = ctx!.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(kind === "incoming" ? 0.12 : 0.05, start + 0.02);
      gain.gain.setValueAtTime(kind === "incoming" ? 0.12 : 0.05, start + length - 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, start + length);
      osc.connect(gain).connect(ctx!.destination);
      osc.start(start);
      osc.stop(start + length + 0.01);
    });
  };
  play();
  const timer = setInterval(play, every);
  return () => clearInterval(timer);
}
